<template>
    <div id="productPriceHistoryModal" aria-hidden="true" aria-labelledby="productPriceHistoryModalLabel" class="modal fade"
         tabindex="-1">
        <div class="modal-dialog modal-xl modal-dialog-scrollable" style="padding-top: 4rem">
            <div class="modal-content shadow-sm rounded-4 overflow-hidden">
                <div class="modal-header bg-light border-bottom" style="padding-top: 16px; padding-bottom: 16px;">
                    <h5 class="modal-title">
                        Product Price History <span class="fw-bold">'{{ product?.basicInfo?.name }}'</span> <span class="fw-bold">SKU: {{ product?.basicInfo?.sku }}</span>
                    </h5>
                    <button aria-label="Close" class="btn-close fs-3 fw-bold" data-bs-dismiss="modal" type="button" style="filter: brightness(0); margin-top: -15px;"></button>
                </div>

                <div class="modal-body p-4">
                    
                    <!-- Channel Selector -->
                    <div class="mb-4">
                        <label class="form-label fw-bold mb-2">Select Sale Channel</label>
                        <div class="d-flex flex-wrap gap-2">
                            <button 
                                v-for="channel in availableChannels" 
                                :key="channel.id" 
                                type="button"
                                :class="['btn btn-channel d-flex flex-column align-items-center justify-content-center', { 'active': selectedChannelId === channel.id }]"
                                @click="selectedChannelId = channel.id"
                            >
                                <span class="mb-1">
                                    <img :src="channel.imageUrl" width="25" :alt="channel.name" v-if="channel.imageUrl">
                                    <span v-else class="fw-bold">{{ getChannelCode(channel.name) }}</span>
                                </span>
                                <span class="text-small text-muted">{{ channel.productPriceId }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>

                    <!-- History Table -->
                    <div v-else>
                        <div v-if="filteredHistory.length > 0" class="table-responsive history-table-container">
                            <table class="table table-bordered table-striped align-middle table-hover mb-0">
                                <thead class="table-light">
                                    <tr>
                                        <th>ID</th>
                                        <th>Previous Original Price</th>
                                        <th>New Original Price</th>
                                        <th>Previous Sale Price</th>
                                        <th>New Sale Price</th>
                                        <th>Variation %</th>
                                        <th>Change Date</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in filteredHistory" :key="item.id">
                                        <td>{{ item.id }}</td>
                                        <td>{{ formatPrice(item.currentPrice) }}</td>
                                        <td>{{ formatPrice(item.newPrice) }}</td>
                                        <td>{{ formatPrice(item.currentSalePrice) }}</td>
                                        <td>{{ formatPrice(item.newSalePrice) }}</td>
                                        <td :class="getVariationClass(item.variationPerc)">
                                            {{ item.variationPerc }}%
                                        </td>
                                        <td>{{ formatDate(item.changedAt) }}</td>
                                        <td>{{ item.reason }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="text-center py-5">
                            <p class="text-muted">No history found for this channel.</p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: "ProductPriceHistoryModal",
    props: {
        channelNames: {
            type: Array,
            default: () => []
        },
        flags: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            product: null,
            history: [],
            loading: false,
            selectedChannelId: null,
            modalInstance: null,
        };
    },
    computed: {
        availableChannels() {
            // Map all available channels from flags prop
            return this.flags.map(flag => {
                // Find associated history to get productPriceId if it exists
                const historyItem = this.history.find(h => h.salesChannelId === flag.code);
                const channelNameObj = this.channelNames.find(c => c.code === flag.code);

                let priceId = '-';
                if (historyItem) {
                    priceId = historyItem.productPriceId;
                } else if (this.product && this.product.prices) {
                    // Fallback: Try to find in current product prices
                    const currentPrice = this.product.prices.find(p => p.saleChannelId === flag.code);
                    if (currentPrice && currentPrice.priceInfo) {
                        priceId = currentPrice.priceInfo.productPriceId;
                    }
                }

                return {
                    id: flag.code,
                    name: channelNameObj ? channelNameObj.label : `Channel ${flag.code}`,
                    imageUrl: flag.label,
                    productPriceId: priceId
                };
            });
        },
        filteredHistory() {
            if (!this.selectedChannelId) return [];
            return this.history.filter(h => h.salesChannelId === this.selectedChannelId);
        }
    },
    methods: {
        async open(product, initialChannelId = null) {
            this.product = product;
            this.history = []; // Reset history
            this.loading = true;
            this.selectedChannelId = null;

            // Show modal immediately
            const modalElement = document.getElementById('productPriceHistoryModal');
            if (modalElement) {
                this.modalInstance = new bootstrap.Modal(modalElement);
                this.modalInstance.show();
            }

            try {
                const response = await axios.get(`/comp/product-price/history/${product.basicInfo.id}`);
                this.history = response.data;

                // Determine selected channel
                if (initialChannelId && this.history.some(h => h.salesChannelId === initialChannelId)) {
                    this.selectedChannelId = initialChannelId;
                } else if (this.availableChannels.length > 0) {
                    this.selectedChannelId = this.availableChannels[0].id;
                }

            } catch (error) {
                console.error("Error fetching price history:", error);
                alert("Failed to load price history.");
            } finally {
                this.loading = false;
            }
        },
        getChannelCode(name) {
             // Extract code like IT, DE, FR from "CMW", "CMW_DE", "CMW FR"
             // This is a heuristic.
             if (name.includes('IT') || name === 'CMW') return 'IT';
             if (name.includes('DE')) return 'DE';
             if (name.includes('FR')) return 'FR';
             if (name.includes('UK')) return 'UK';
             if (name.includes('B2B')) return 'B2B';
             return name.substring(0, 2).toUpperCase();
        },
        formatPrice(value) {
            if (value === null || value === undefined) return '-';
            return parseFloat(value).toFixed(2) + ' €';
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            const pad = (n) => (n < 10 ? '0' + n : n);
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        },
        getVariationClass(perc) {
            const val = parseFloat(perc);
            if (val > 0) return 'text-success fw-bold';
            if (val < 0) return 'text-danger fw-bold';
            return 'text-muted';
        }
    }
}
</script>

<style scoped>
.modal-body {
    background-color: #f9f9f9;
}
.history-table-container {
    max-height: 46vh;
    overflow-y: auto;
    border: 1px solid #dee2e6;
}
.btn-channel {
    width: 60px;
    height: 60px;
    border: 1px solid #dee2e6;
    background-color: #fff;
    border-radius: 8px;
    transition: all 0.2s;
    padding: 0;
}
.btn-channel:hover {
    border-color: #adb5bd;
    background-color: #f8f9fa;
}
.btn-channel.active {
    border: 2px solid #dc3545; /* Red border for active state */
    background-color: #fff;
    color: #000;
}
.text-small {
    font-size: 0.7rem;
}
.table th {
    font-size: 0.75rem; /* Reduced from 0.85rem */
    text-transform: uppercase;
    color: #6c757d;
    vertical-align: top;
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f8f9fa; /* Match table-light */
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
}
.table td {
    font-size: 0.8rem; /* Reduced from 0.9rem */
}
</style>
